import { parseRoles } from "./roles";
import { expectArray, expectBoolean, expectOptionalString, expectRecord, expectString } from "./schema";
export const VIDEO_PURPOSES = ["lessonSupport", "replacement", "eventSupport"];
export const VIDEO_TARGET_TYPES = ["lesson", "occurrence", "event"];
export const VIDEO_UPLOAD_STATUSES = ["draft", "uploadIssued", "uploaded", "failed"];
export const VIDEO_PUBLISH_STATUSES = ["draft", "scheduled", "published", "hidden", "expired", "deleted"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
function expectOptionalNumber(value, path) {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new Error(`${path} must be a finite number`);
    }
    return value;
}
export function parseVideoPurpose(value, path = "videoPurpose") {
    return parseStringEnum(value, VIDEO_PURPOSES, path);
}
export function parseVideoTargetType(value, path = "videoTargetType") {
    return parseStringEnum(value, VIDEO_TARGET_TYPES, path);
}
export function parseVideoUploadStatus(value, path = "videoUploadStatus") {
    return parseStringEnum(value, VIDEO_UPLOAD_STATUSES, path);
}
export function parseVideoPublishStatus(value, path = "videoPublishStatus") {
    return parseStringEnum(value, VIDEO_PUBLISH_STATUSES, path);
}
export function parseAdminVideoListItem(value, path = "adminVideoListItem") {
    const record = expectRecord(value, path);
    return {
        videoId: expectString(record.videoId, `${path}.videoId`),
        title: expectOptionalString(record.title, `${path}.title`),
        titleSource: expectOptionalString(record.titleSource, `${path}.titleSource`),
        description: expectOptionalString(record.description, `${path}.description`),
        videoPurpose: parseVideoPurpose(record.videoPurpose, `${path}.videoPurpose`),
        targetType: parseVideoTargetType(record.targetType, `${path}.targetType`),
        lessonId: expectOptionalString(record.lessonId, `${path}.lessonId`),
        occurrenceId: expectOptionalString(record.occurrenceId, `${path}.occurrenceId`),
        eventId: expectOptionalString(record.eventId, `${path}.eventId`),
        targetLabel: expectString(record.targetLabel, `${path}.targetLabel`),
        uploadStatus: parseVideoUploadStatus(record.uploadStatus, `${path}.uploadStatus`),
        publishStatus: parseVideoPublishStatus(record.publishStatus, `${path}.publishStatus`),
        originalFileName: expectOptionalString(record.originalFileName, `${path}.originalFileName`),
        mimeType: expectOptionalString(record.mimeType, `${path}.mimeType`),
        fileSizeBytes: expectOptionalNumber(record.fileSizeBytes, `${path}.fileSizeBytes`),
        durationSeconds: expectOptionalNumber(record.durationSeconds, `${path}.durationSeconds`),
        publishFrom: expectOptionalString(record.publishFrom, `${path}.publishFrom`),
        publishTo: expectOptionalString(record.publishTo, `${path}.publishTo`),
        autoPublishedAt: expectOptionalString(record.autoPublishedAt, `${path}.autoPublishedAt`),
        publishErrorCodes: expectArray(record.publishErrorCodes, expectString, `${path}.publishErrorCodes`),
        publishWarningCodes: expectArray(record.publishWarningCodes, expectString, `${path}.publishWarningCodes`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseAdminVideosResponse(value, path = "adminVideosResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminVideoListItem, `${path}.items`)
    };
}
export function parseAdminVideoDetail(value, path = "adminVideoDetail") {
    const record = expectRecord(value, path);
    const listItem = parseAdminVideoListItem(record, path);
    return {
        ...listItem,
        objectPath: expectOptionalString(record.objectPath, `${path}.objectPath`),
        retentionUntil: expectOptionalString(record.retentionUntil, `${path}.retentionUntil`),
        visibleToRoles: parseRoles(record.visibleToRoles, `${path}.visibleToRoles`),
        allowTrialViewer: expectBoolean(record.allowTrialViewer, `${path}.allowTrialViewer`),
        requireActiveEnrollment: expectBoolean(record.requireActiveEnrollment, `${path}.requireActiveEnrollment`),
        uploadedByPlatformUserId: expectString(record.uploadedByPlatformUserId, `${path}.uploadedByPlatformUserId`),
        uploadedByRoleSnapshot: expectString(record.uploadedByRoleSnapshot, `${path}.uploadedByRoleSnapshot`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`)
    };
}
export function parseCreateAdminVideoRequest(value, path = "createAdminVideoRequest") {
    const record = expectRecord(value, path);
    const targetType = parseVideoTargetType(record.targetType, `${path}.targetType`);
    const lessonId = expectOptionalString(record.lessonId, `${path}.lessonId`);
    const occurrenceId = expectOptionalString(record.occurrenceId, `${path}.occurrenceId`);
    const eventId = expectOptionalString(record.eventId, `${path}.eventId`);
    if (targetType === "lesson" && !lessonId) {
        throw new Error(`${path}.lessonId is required when targetType=lesson`);
    }
    if (targetType === "occurrence" && !occurrenceId) {
        throw new Error(`${path}.occurrenceId is required when targetType=occurrence`);
    }
    if (targetType === "event" && !eventId) {
        throw new Error(`${path}.eventId is required when targetType=event`);
    }
    return {
        videoPurpose: parseVideoPurpose(record.videoPurpose, `${path}.videoPurpose`),
        targetType,
        title: expectOptionalString(record.title, `${path}.title`),
        description: expectOptionalString(record.description, `${path}.description`),
        publishFrom: expectOptionalString(record.publishFrom, `${path}.publishFrom`),
        publishTo: expectOptionalString(record.publishTo, `${path}.publishTo`),
        lessonId,
        occurrenceId,
        eventId
    };
}
export function parseUpdateAdminVideoRequest(value, path = "updateAdminVideoRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "title")) {
        parsed.title = expectOptionalString(record.title, `${path}.title`);
    }
    if (hasOwn(record, "description")) {
        parsed.description = expectOptionalString(record.description, `${path}.description`);
    }
    if (hasOwn(record, "publishFrom")) {
        parsed.publishFrom = expectOptionalString(record.publishFrom, `${path}.publishFrom`);
    }
    if (hasOwn(record, "publishTo")) {
        parsed.publishTo = expectOptionalString(record.publishTo, `${path}.publishTo`);
    }
    return parsed;
}
export function parseVideoUploadSessionResponse(value, path = "videoUploadSessionResponse") {
    const record = expectRecord(value, path);
    return {
        videoId: expectString(record.videoId, `${path}.videoId`),
        uploadUrl: expectString(record.uploadUrl, `${path}.uploadUrl`),
        uploadSessionToken: expectString(record.uploadSessionToken, `${path}.uploadSessionToken`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`)
    };
}
export function parseCompleteVideoUploadRequest(value, path = "completeVideoUploadRequest") {
    const record = expectRecord(value, path);
    const parsed = {
        uploadSessionToken: expectString(record.uploadSessionToken, `${path}.uploadSessionToken`)
    };
    if (hasOwn(record, "originalFileName")) {
        parsed.originalFileName = expectOptionalString(record.originalFileName, `${path}.originalFileName`);
    }
    if (hasOwn(record, "mimeType")) {
        parsed.mimeType = expectOptionalString(record.mimeType, `${path}.mimeType`);
    }
    if (hasOwn(record, "fileSizeBytes")) {
        parsed.fileSizeBytes = expectOptionalNumber(record.fileSizeBytes, `${path}.fileSizeBytes`);
    }
    if (hasOwn(record, "durationSeconds")) {
        parsed.durationSeconds = expectOptionalNumber(record.durationSeconds, `${path}.durationSeconds`);
    }
    return parsed;
}
export function parseCompleteVideoUploadResponse(value, path = "completeVideoUploadResponse") {
    const record = expectRecord(value, path);
    return {
        video: parseAdminVideoDetail(record.video, `${path}.video`),
        autoPublished: expectBoolean(record.autoPublished, `${path}.autoPublished`)
    };
}
export function parseViewerVideoListItem(value, path = "viewerVideoListItem") {
    const record = expectRecord(value, path);
    return {
        videoId: expectString(record.videoId, `${path}.videoId`),
        title: expectOptionalString(record.title, `${path}.title`),
        description: expectOptionalString(record.description, `${path}.description`),
        videoPurpose: parseVideoPurpose(record.videoPurpose, `${path}.videoPurpose`),
        targetType: parseVideoTargetType(record.targetType, `${path}.targetType`),
        targetLabel: expectString(record.targetLabel, `${path}.targetLabel`),
        lessonId: expectOptionalString(record.lessonId, `${path}.lessonId`),
        occurrenceId: expectOptionalString(record.occurrenceId, `${path}.occurrenceId`),
        eventId: expectOptionalString(record.eventId, `${path}.eventId`),
        durationSeconds: expectOptionalNumber(record.durationSeconds, `${path}.durationSeconds`),
        publishFrom: expectOptionalString(record.publishFrom, `${path}.publishFrom`),
        publishTo: expectOptionalString(record.publishTo, `${path}.publishTo`)
    };
}
export function parseViewerVideosResponse(value, path = "viewerVideosResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseViewerVideoListItem, `${path}.items`)
    };
}
export function parseViewerVideoDetail(value, path = "viewerVideoDetail") {
    const record = expectRecord(value, path);
    const listItem = parseViewerVideoListItem(record, path);
    return {
        ...listItem,
        playbackAvailable: expectBoolean(record.playbackAvailable, `${path}.playbackAvailable`)
    };
}
export function parsePlaybackUrlResponse(value, path = "playbackUrlResponse") {
    const record = expectRecord(value, path);
    return {
        videoId: expectString(record.videoId, `${path}.videoId`),
        playbackUrl: expectString(record.playbackUrl, `${path}.playbackUrl`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`)
    };
}
